/**
 * formatCurrency — Unified currency formatting utility
 *
 * Two modes:
 * - 'aggregate' (default): whole dollars for headers/totals → "$505"
 * - 'transaction': with cents for individual rows → "$5.40"
 *
 * Always uses Math.abs() to normalize — sign/color is determined by
 * category context, not the raw amount.
 */

export function formatCurrency(
  cents: number,
  mode: 'transaction' | 'aggregate' = 'aggregate'
): string {
  const dollars = Math.abs(cents) / 100;
  if (mode === 'transaction') {
    return dollars.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return `$${Math.round(dollars).toLocaleString()}`;
}

/**
 * formatSignedCurrency — Includes +/- prefix based on sign of cents value.
 * Uses 'aggregate' mode (whole dollars).
 */
export function formatSignedCurrency(cents: number): string {
  const abs = Math.round(Math.abs(cents) / 100);
  return cents >= 0 ? `+$${abs.toLocaleString()}` : `-$${abs.toLocaleString()}`;
}

/**
 * formatHourlyRate — Convenience for cents-per-hour display.
 */
export function formatHourlyRate(centsPerHour: number): string {
  return `${formatCurrency(centsPerHour)}/hr`;
}
